import type {
  AllocateRequest,
  AllocateResponse,
  AllocationsListResponse,
  DeallocateRequest,
  DeallocateResponse,
  PortfolioSummary,
  StrategyAllocation,
} from "@/types/api.types";
import { API_BASE_URL, fetchWithTimeout, handleResponse } from "./shared";

// ============================================================================
// Allocations API
// ============================================================================

export const allocationsApi = {
  /**
   * List allocations for the authenticated user (session cookie)
   * Falls back to wallet address if provided
   */
  async list(walletAddress?: string): Promise<AllocationsListResponse> {
    const url = walletAddress
      ? `${API_BASE_URL}/api/allocations?walletAddress=${encodeURIComponent(walletAddress)}`
      : `${API_BASE_URL}/api/allocations`;
    const response = await fetchWithTimeout(url);
    // Backend returns AllocationDto[] directly, wrap it
    const allocations = await handleResponse<StrategyAllocation[]>(response);
    return { allocations };
  },

  /**
   * Create a new allocation
   */
  async create(data: AllocateRequest): Promise<AllocateResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/allocations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<AllocateResponse>(response);
  },

  /**
   * Deallocate (withdraw) from an allocation
   */
  async deallocate(id: string, data: DeallocateRequest): Promise<DeallocateResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/allocations/${id}/deallocate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<DeallocateResponse>(response);
  },

  /**
   * Get portfolio summary for the authenticated user (session cookie)
   * Falls back to wallet address if provided
   */
  async getPortfolio(walletAddress?: string): Promise<PortfolioSummary> {
    // Portfolio summary — session cookie handles auth, wallet is optional fallback
    const portfolioUrl = walletAddress
      ? `${API_BASE_URL}/api/allocations/portfolio?walletAddress=${encodeURIComponent(walletAddress)}`
      : `${API_BASE_URL}/api/allocations/portfolio`;

    const portfolioRes = await fetchWithTimeout(portfolioUrl);

    const data = await handleResponse<
      Omit<PortfolioSummary, "availableBalance"> & { allocations?: unknown[] }
    >(portfolioRes);

    return {
      totalAllocated: data.totalAllocated,
      totalCurrentValue: data.totalCurrentValue,
      totalUnrealizedPnl: data.totalUnrealizedPnl,
      totalUnrealizedPnlPercent: data.totalUnrealizedPnlPercent,
      totalRealizedPnl: data.totalRealizedPnl,
      allocationCount: data.allocationCount,
      availableBalance: null, // Will be populated from account data
    };
  },
};
